import { useState, useCallback } from 'react';
import { Modal, message } from 'antd';
import { j203Api, FJ130, ApprovalHistory } from '../../../services/j203Api';

interface UseJ203ApprovalProps {
  record: FJ130 | null;
  isEditing: boolean;
  loadRecord: (id: string) => Promise<FJ130 | null>;
}

export function useJ203Approval({ record, isEditing, loadRecord }: UseJ203ApprovalProps) {
  const [historyVisible, setHistoryVisible] = useState(false);
  const [historyData, setHistoryData] = useState<ApprovalHistory[]>([]);

  const handleSubmit = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認送審',
      content: `確定要送審交運單 ${record.fj130_01}？`,
      okText: '送審',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await j203Api.submitForApproval(record.fj130_01);
          message.success(resp.message);
          await loadRecord(record.fj130_01);
        } catch (e: unknown) {
          const err = e as { response?: { data?: { error?: string } } };
          message.error(err.response?.data?.error || '送審失敗');
        }
      },
    });
  }, [record, isEditing, loadRecord]);

  const handleApprove = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認核准',
      content: `確定要核准交運單 ${record.fj130_01}？`,
      okText: '核准',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await j203Api.approve(record.fj130_01);
          message.success(resp.message);
          await loadRecord(record.fj130_01);
        } catch (e: unknown) {
          const err = e as { response?: { data?: { error?: string } } };
          message.error(err.response?.data?.error || '核准失敗');
        }
      },
    });
  }, [record, isEditing, loadRecord]);

  const handleReject = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認退回',
      content: `確定要退回交運單 ${record.fj130_01}？`,
      okText: '退回',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await j203Api.reject(record.fj130_01);
          message.success(resp.message);
          await loadRecord(record.fj130_01);
        } catch (e: unknown) {
          const err = e as { response?: { data?: { error?: string } } };
          message.error(err.response?.data?.error || '退回失敗');
        }
      },
    });
  }, [record, isEditing, loadRecord]);

  const handleViewHistory = useCallback(async () => {
    if (!record) return;
    try {
      const data = await j203Api.getApprovalHistory(record.fj130_01);
      setHistoryData(data);
      setHistoryVisible(true);
    } catch {
      message.error('載入審核歷史失敗');
    }
  }, [record]);

  const handleWriteoff = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷此單據 ${record.fj130_01}？沖銷後可重新送審。`,
      okText: '沖銷',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await j203Api.writeoff(record.fj130_01);
          message.success(resp.message || '沖銷成功');
          await loadRecord(record.fj130_01);
        } catch (e: unknown) {
          const err = e as { response?: { data?: { error?: string } } };
          message.error(err.response?.data?.error || '沖銷失敗');
        }
      },
    });
  }, [record, isEditing, loadRecord]);

  return {
    handleSubmit, handleApprove, handleReject, handleWriteoff, handleViewHistory,
    historyVisible, setHistoryVisible, historyData,
  };
}
